import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { RECORRIDO } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Trash2, Printer, Footprints, TriangleAlert as AlertTriangle, MapPin, Calendar, User } from "lucide-react";

export default function RecorridoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDelete = ["admin", "supervisor"].includes(user?.role);

  const { data: r, isLoading } = useQuery({
    queryKey: ["recorrido", id],
    queryFn: () => api.get(`/recorridos`).then((res) => res.data.find((x) => x.id === id)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/recorridos/${id}`).then((res) => res.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recorridos"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Recorrido eliminado");
      navigate("/recorridos");
    },
    onError: (e) => toast.error(apiError(e, "Error al eliminar")),
  });

  if (isLoading) return <div className="p-8 text-muted-foreground">Cargando…</div>;
  if (!r) return <div className="p-8 text-muted-foreground">Recorrido no encontrado.</div>;

  const print = () => window.print();

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4 print:hidden">
        <Link
          to="/recorridos"
          data-testid={RECORRIDO.detailBack}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-navy"
        >
          <ArrowLeft size={16} /> Volver a recorridos
        </Link>
        <div className="flex gap-2">
          <button
            onClick={print}
            data-testid={RECORRIDO.reportButton}
            className="flex items-center gap-2 h-10 px-4 border border-navy text-navy font-medium hover:bg-navy hover:text-white transition-colors"
          >
            <Printer size={16} /> Generar reporte
          </button>
          {canDelete && (
            <button
              onClick={() => deleteMutation.mutate()}
              data-testid={RECORRIDO.deleteButton}
              className="flex items-center gap-2 h-10 px-4 border border-critical text-critical font-medium hover:bg-critical hover:text-white transition-colors"
            >
              <Trash2 size={16} /> Eliminar
            </button>
          )}
        </div>
      </div>

      <article className="bg-white border border-border p-6 lg:p-8">
        <header className="border-b border-border pb-5 mb-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Reporte de recorrido</p>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight text-navy">{r.titulo}</h1>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium ${
              r.estado === "con_novedad" ? "bg-critical/10 text-critical" : "bg-success/10 text-success"
            }`}>
              {r.estado === "con_novedad" ? <AlertTriangle size={14} /> : <Footprints size={14} />}
              {r.estado === "con_novedad" ? `Con novedad · ${r.severidad}` : "Sin novedad"}
            </span>
          </div>
        </header>

        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <InfoRow icon={MapPin} label="Ubicación" value={r.ubicacion || "—"} />
          <InfoRow icon={Calendar} label="Fecha" value={r.fecha ? new Date(r.fecha).toLocaleString("es-MX") : "—"} />
          <InfoRow icon={User} label="Registrado por" value={r.created_by} />
          <InfoRow icon={AlertTriangle} label="Severidad" value={r.severidad || "—"} />
        </div>

        {r.novedad && (
          <section className="mb-6">
            <h2 className="font-heading text-lg font-bold text-navy mb-2">Descripción de la novedad</h2>
            <p className="text-sm text-foreground whitespace-pre-wrap border-l-4 border-critical pl-4 py-2 bg-critical/5">
              {r.novedad}
            </p>
          </section>
        )}

        {r.fotos?.length > 0 && (
          <section>
            <h2 className="font-heading text-lg font-bold text-navy mb-3">Fotografías</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {r.fotos.map((src, idx) => (
                <a key={idx} href={src} target="_blank" rel="noreferrer" className="block border border-border overflow-hidden">
                  <img src={src} alt={`Foto ${idx + 1}`} className="w-full aspect-video object-cover" />
                </a>
              ))}
            </div>
          </section>
        )}

        <footer className="mt-8 pt-5 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
          <span>ID: {r.id}</span>
          <span>SISP · Sistema Integral de Seguridad Patrimonial</span>
        </footer>
      </article>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 p-3 border border-border bg-secondary/30">
      <span className="w-8 h-8 bg-white text-navy flex items-center justify-center shrink-0 border border-border">
        <Icon size={16} />
      </span>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-navy truncate">{value}</p>
      </div>
    </div>
  );
}
